import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

// Import your page components
import About from './pages/About';
import Classroom from './pages/Classroom';
import Giveaway from './pages/Giveaway';
import Games from './pages/Games';
import HowTo from './pages/HowTo';
import UseCases from './pages/UseCases';

// Colors for wheel sections
const COLORS = [
  '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', 
  '#9966FF', '#FF9F40', '#8AC926', '#FF595E',
  '#6A4C93', '#1982C4', '#FFCA3A', '#FF85EA'
];

// Confetti Component
const Confetti = () => {
  // Create confetti pieces
  const createConfetti = () => {
    const confetti = [];
    const colors = ['#3A9679', '#EF767A', '#456990', '#EEB868', '#BC96E6', '#7393B3'];
    
    for (let i = 0; i < 100; i++) {
      const left = Math.random() * 100;
      const width = Math.random() * 10 + 5;
      const height = Math.random() * 10 + 5;
      const bg = colors[Math.floor(Math.random() * colors.length)];
      const delay = Math.random() * 5;
      
      confetti.push(
        <div
          key={i}
          className="confetti"
          style={{
            position: 'absolute',
            left: `${left}%`,
            top: '-20px',
            width: `${width}px`,
            height: `${height}px`,
            backgroundColor: bg,
            borderRadius: '50%',
            animation: `fall 5s linear ${delay}s`
          }}
        />
      );
    }
    
    return confetti;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      pointerEvents: 'none',
      overflow: 'hidden'
    }}>
      {createConfetti()}
    </div>
  );
};

// NamesList Component
const NamesList = ({ 
  names, 
  colors, 
  newName, 
  setNewName, 
  handleAddName, 
  handleDeleteName, 
  handleKeyDown 
}) => {
  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      padding: '20px',
      marginBottom: '20px',
      marginTop: '20px',
      width: '100%',
      maxWidth: '400px'
    }}>
      <h2 style={{
        fontSize: '20px',
        fontWeight: 'bold',
        marginBottom: '16px',
        color: '#333'
      }}>Names</h2>
      
      <div style={{
        display: 'flex',
        marginBottom: '16px'
      }}>
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter a name"
          style={{
            flexGrow: 1,
            padding: '8px 12px',
            border: '1px solid #ccc',
            borderRadius: '4px 0 0 4px',
            fontSize: '16px'
          }}
        />
        <button
          onClick={handleAddName}
          style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            padding: '8px 16px',
            border: 'none',
            borderRadius: '0 4px 4px 0',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Add
        </button>
      </div>
      
      <div style={{
        maxHeight: '300px',
        overflowY: 'auto'
      }}>
        {names.map((name, index) => (
          <div key={index} style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px',
            borderRadius: '4px',
            backgroundColor: index % 2 === 0 ? '#f9fafb' : 'transparent'
          }}>
            <span style={{ color: colors[index % colors.length] }}>
              {name}
            </span>
            <button 
              onClick={() => handleDeleteName(index)}
              style={{
                color: '#ef4444',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Wheel Component
const Wheel = ({ names, colors, rotationAngle }) => {
  // Calculate wheel segments based on names
  const calcDegreePerName = () => 360 / names.length;
  
  // Draw the wheel using SVG
  const drawWheel = () => {
    const degreePerName = calcDegreePerName();
    const radius = 150;
    const center = 200;
    
    return names.map((name, index) => {
      const startAngle = index * degreePerName;
      const endAngle = (index + 1) * degreePerName;
      
      // Calculate SVG arc path
      const startRad = (startAngle - 90) * (Math.PI / 180);
      const endRad = (endAngle - 90) * (Math.PI / 180);
      
      const x1 = center + radius * Math.cos(startRad);
      const y1 = center + radius * Math.sin(startRad);
      
      const x2 = center + radius * Math.cos(endRad);
      const y2 = center + radius * Math.sin(endRad);
      
      const largeArcFlag = degreePerName > 180 ? 1 : 0;
      
      // Create path for pie slice
      const path = [
        `M ${center} ${center}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z'
      ].join(' ');
      
      // Calculate position for text
      const textRad = (startAngle + degreePerName / 2 - 90) * (Math.PI / 180);
      const textX = center + (radius * 0.65) * Math.cos(textRad);
      const textY = center + (radius * 0.65) * Math.sin(textRad);
      
      // Calculate text rotation
      const textRotation = startAngle + degreePerName / 2;
      
      return (
        <g key={index}>
          <path
            d={path}
            fill={colors[index % colors.length]}
            stroke="#fff"
            strokeWidth="2"
          />
          <text
            x={textX}
            y={textY}
            fill="#fff"
            fontWeight="bold"
            fontSize="14"
            textAnchor="middle"
            alignmentBaseline="middle"
            transform={`rotate(${textRotation}, ${textX}, ${textY})`}
          >
            {name}
          </text>
        </g>
      );
    });
  };

  return (
    <svg 
      width="400"
      height="400"
      viewBox="0 0 400 400"
      style={{
        transform: `rotate(${rotationAngle}deg)`,
        transition: 'transform 5s cubic-bezier(0.17, 0.67, 0.83, 0.67)',
        maxWidth: '90vw',
        maxHeight: '90vw'
      }}
    >
      {drawWheel()}
      <circle cx="200" cy="200" r="15" fill="#333" />
    </svg>
  );
};

// Main SpinningWheel Component
const SpinningWheel = () => {
  const [names, setNames] = useState(['Alice', 'Bob', 'Charlie', 'David']);
  const [newName, setNewName] = useState('');
  const [spinning, setSpinning] = useState(false);
  const [winner, setWinner] = useState(null);
  const [rotationAngle, setRotationAngle] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const cheerAudioRef = useRef(null);
  const clickingAudioRef = useRef(null);

  // Load audio effects
  useEffect(() => {
    // Cheer sound effect
    const cheerAudio = new Audio();
    cheerAudio.src = '../cheers.mp3'; // Would be replaced with actual path in production
    cheerAudioRef.current = cheerAudio;
    
    // Clicking sound effect for the spinning wheel
    const clickingAudio = new Audio();
    clickingAudio.src = '../spinner.mp3'; // Example URL for clicking sound
    clickingAudio.loop = true; // Make the clicking sound loop during spinning
    clickingAudioRef.current = clickingAudio;
  }, []);

  const handleAddName = () => {
    if (newName.trim() !== '') {
      setNames([...names, newName.trim()]);
      setNewName('');
    }
  };

  const handleDeleteName = (index) => {
    const newNames = [...names];
    newNames.splice(index, 1);
    setNames(newNames);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddName();
    }
  };

  const spinWheel = () => {
    if (spinning || names.length < 2) return;
    
    setSpinning(true);
    setWinner(null);
    setShowConfetti(false);
    
    // Start playing the clicking sound when the wheel starts spinning
    try {
      if (clickingAudioRef.current) {
        clickingAudioRef.current.currentTime = 0;
        clickingAudioRef.current.play().catch(e => console.log('Clicking audio play error:', e));
      }
    } catch (e) {
      console.log('Clicking audio error:', e);
    }
    
    // Calculate a random spin between 5-10 full rotations plus a random segment
    const spinRotations = 5 + Math.random() * 5;
    const randomAngle = Math.random() * 360;
    const totalRotation = 360 * spinRotations + randomAngle;
    
    // Set the new rotation angle
    setRotationAngle(rotationAngle + totalRotation);
    
    // Determine the winner after the spin
    setTimeout(() => {
      // Stop the clicking sound when the wheel stops
      if (clickingAudioRef.current) {
        clickingAudioRef.current.pause();
        clickingAudioRef.current.currentTime = 0;
      }
      
      // Play the cheer sound
      try {
        if (cheerAudioRef.current) {
          cheerAudioRef.current.currentTime = 0;
          cheerAudioRef.current.play().catch(e => console.log('Cheer audio play error:', e));
        }
      } catch (e) {
        console.log('Cheer audio error:', e);
      }
      
      const degreePerName = 360 / names.length;
      const normalizedAngle = (rotationAngle + totalRotation) % 360;
      const winnerIndex = Math.floor(names.length - (normalizedAngle / degreePerName)) % names.length;
      setWinner(names[winnerIndex]);
      setShowConfetti(true);
      setSpinning(false);
    }, 5000); // Match this with the CSS transition time
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '10px',
      fontFamily: 'Arial, sans-serif',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto'
    }}>
      <h1 style={{
        fontSize: '24px',
        fontWeight: 'bold',
        margin: '5px 0 10px',
        color: '#333',
        textAlign: 'center'
      }}>Wheel Of Names</h1>
      
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '20px',
        flex: 1,
        flexWrap: 'wrap'
      }}>
        {/* Wheel Section */}
        <div style={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          maxWidth: '100%'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '12px solid transparent',
            borderRight: '12px solid transparent',
            borderTop: '24px solid red',
            zIndex: 10
          }} />
          <Wheel 
            names={names}
            colors={COLORS}
            rotationAngle={rotationAngle}
          />
          
          {/* Winner announcement shown over the wheel */}
          {winner && (
            <div style={{
              position: 'absolute',
              bottom: '10%',
              textAlign: 'center',
              width: '100%',
              backgroundColor: 'rgba(255,255,255,0.8)',
              padding: '5px',
              borderRadius: '5px'
            }}>
              <h2 
                style={{ 
                  fontSize: '20px',
                  fontWeight: 'bold',
                  margin: '5px',
                  color: COLORS[names.indexOf(winner) % COLORS.length] 
                }}
              >
                {winner} wins!
              </h2>
            </div>
          )}
          
          {/* Spin Button moved into wheel container */}
          <div style={{ 
            position: 'absolute', 
            bottom: '-20px'
          }}>
            <button
              onClick={spinWheel}
              disabled={spinning || names.length < 2}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#dc2626',
                color: 'white',
                fontSize: '18px',
                fontWeight: 'bold',
                padding: '12px 40px',
                border: 'none',
                borderRadius: '9999px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                cursor: spinning || names.length < 2 ? 'not-allowed' : 'pointer',
                opacity: spinning || names.length < 2 ? 0.5 : 1,
                animation: !spinning && names.length >= 2 ? 'pulse 2s infinite' : 'none',
              }}
            >
              {spinning ? 'Spinning...' : 'SPIN'}
              {!spinning && (
                <svg 
                  style={{ marginLeft: '8px' }} 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
              )}
            </button>
          </div>
        </div>
        
        {/* Names Input Section */}
        <NamesList 
          names={names}
          colors={COLORS}
          newName={newName}
          setNewName={setNewName}
          handleAddName={handleAddName}
          handleDeleteName={handleDeleteName}
          handleKeyDown={handleKeyDown}
        />
      </div>
      
      {names.length < 2 && (
        <p style={{
          color: '#ef4444',
          textAlign: 'center',
          margin: '5px 0'
        }}>Add at least 2 names to spin the wheel</p>
      )}
      
      {/* Marketing Content Section */}
      <div style={{
        maxWidth: '1000px',
        margin: '40px auto 20px',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        textAlign: 'left',
        lineHeight: '1.6'
      }}>
        <h2 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          color: '#333',
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          🎡 Spin the Wheel of Names – Your Ultimate Random Name Picker Tool
        </h2>
        
        <p style={{
          fontSize: '16px',
          color: '#555',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          Welcome to <strong>Wheels of Names</strong>, the premier online destination for all your random selection needs. Whether you're a teacher seeking a <strong>classroom name spinner</strong>, a streamer organizing a giveaway, or someone making everyday decisions, our <strong>free online spinner</strong> is here to assist.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '30px',
          marginBottom: '30px'
        }}>
          <div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#dc2626',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🎯 Why Choose Our Wheel Spinner?
            </h3>
            <p style={{
              fontSize: '15px',
              color: '#666',
              marginBottom: '16px'
            }}>
              Our <strong>random name picker</strong> is intuitive and versatile, catering to various scenarios:
            </p>
            <ul style={{
              fontSize: '15px',
              color: '#666',
              paddingLeft: '0',
              listStyle: 'none'
            }}>
              <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: '0', color: '#dc2626' }}>•</span>
                <strong>Classroom Engagement</strong>: Educators can use our <strong>student name randomizer</strong> to promote participation and fairness.
              </li>
              <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: '0', color: '#dc2626' }}>•</span>
                <strong>Event Planning</strong>: Organize raffles or prize distributions effortlessly with our <strong>giveaway wheel spinner</strong>.
              </li>
              <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: '0', color: '#dc2626' }}>•</span>
                <strong>Decision Making</strong>: Simplify choices with our <strong>decision spinner tool</strong>, perfect for both personal and professional use.
              </li>
            </ul>
          </div>

          <div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#dc2626',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🛠️ Features Tailored for You
            </h3>
            <ul style={{
              fontSize: '15px',
              color: '#666',
              paddingLeft: '0',
              listStyle: 'none'
            }}>
              <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: '0', color: '#dc2626' }}>•</span>
                <strong>Customizable Entries</strong>: Easily add or remove names to suit your specific needs.
              </li>
              <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: '0', color: '#dc2626' }}>•</span>
                <strong>User-Friendly Interface</strong>: Designed for seamless use across all devices.
              </li>
              <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: '0', color: '#dc2626' }}>•</span>
                <strong>No Downloads Required</strong>: Access our <strong>name selector wheel online</strong> anytime, anywhere.
              </li>
            </ul>
          </div>
        </div>

        <div style={{
          textAlign: 'center',
          padding: '20px',
          backgroundColor: '#dc2626',
          color: 'white',
          borderRadius: '8px',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          Experience the ease and efficiency of our <strong>free online spinner for names</strong> today!
        </div>
      </div>
      
      {/* Confetti container */}
      {showConfetti && <Confetti />}
      
      {/* CSS for confetti animation */}
      <style>
        {`
          @keyframes fall {
            0% { 
              transform: translateY(0) rotate(0deg); 
              opacity: 1;
            }
            100% { 
              transform: translateY(100vh) rotate(360deg); 
              opacity: 0;
            }
          }
          
          @keyframes pulse {
            0% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
            100% {
              transform: scale(1);
            }
          }
          
          body {
            margin: 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
              'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
              sans-serif;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            background-color: #f5f5f5;
          }
          
          @media (max-width: 768px) {
            .content-container {
              flex-direction: column;
              align-items: center;
            }
          }
          
          @media (min-width: 768px) {
            .content-container {
              flex-direction: row;
              justify-content: center;
              gap: 30px;
            }
          }
        `}
      </style>
    </div>
  );
};

// Main App Component with Navigation
function App() {
  const [currentPage, setCurrentPage] = useState('home');

  // Function to render the current page
  const renderPage = () => {
    switch(currentPage) {
      case 'about': return <About />;
      case 'classroom': return <Classroom />;
      case 'giveaway': return <Giveaway />;
      case 'games': return <Games />;
      case 'how-to': return <HowTo />;
      case 'use-cases': return <UseCases />;
      default: return <SpinningWheel />;
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Navigation */}
      <nav style={{ 
        backgroundColor: 'white', 
        padding: '15px 20px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          display: 'flex', 
          alignItems: 'center',
          gap: '30px',
          flexWrap: 'wrap'
        }}>
          <button 
            onClick={() => setCurrentPage('home')} 
            style={{ 
              background: 'none', 
              border: 'none', 
              color: '#dc2626', 
              fontSize: '24px', 
              fontWeight: 'bold', 
              cursor: 'pointer',
              padding: '5px 0'
            }}
          >
            🎡 Wheel of Names
          </button>
          
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <button 
              onClick={() => setCurrentPage('about')} 
              style={{ 
                background: 'none', 
                border: 'none', 
                color: currentPage === 'about' ? '#dc2626' : '#333', 
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: currentPage === 'about' ? 'bold' : 'normal',
                padding: '5px 10px',
                borderRadius: '4px',
                backgroundColor: currentPage === 'about' ? '#fef2f2' : 'transparent'
              }}
            >
              About
            </button>
            <button 
              onClick={() => setCurrentPage('classroom')} 
              style={{ 
                background: 'none', 
                border: 'none', 
                color: currentPage === 'classroom' ? '#dc2626' : '#333', 
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: currentPage === 'classroom' ? 'bold' : 'normal',
                padding: '5px 10px',
                borderRadius: '4px',
                backgroundColor: currentPage === 'classroom' ? '#fef2f2' : 'transparent'
              }}
            >
              Teachers
            </button>
            <button 
              onClick={() => setCurrentPage('giveaway')} 
              style={{ 
                background: 'none', 
                border: 'none', 
                color: currentPage === 'giveaway' ? '#dc2626' : '#333', 
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: currentPage === 'giveaway' ? 'bold' : 'normal',
                padding: '5px 10px',
                borderRadius: '4px',
                backgroundColor: currentPage === 'giveaway' ? '#fef2f2' : 'transparent'
              }}
            >
              Giveaways
            </button>
            <button 
              onClick={() => setCurrentPage('games')} 
              style={{ 
                background: 'none', 
                border: 'none', 
                color: currentPage === 'games' ? '#dc2626' : '#333', 
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: currentPage === 'games' ? 'bold' : 'normal',
                padding: '5px 10px',
                borderRadius: '4px',
                backgroundColor: currentPage === 'games' ? '#fef2f2' : 'transparent'
              }}
            >
              Games
            </button>
            <button 
              onClick={() => setCurrentPage('how-to')} 
              style={{ 
                background: 'none', 
                border: 'none', 
                color: currentPage === 'how-to' ? '#dc2626' : '#333', 
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: currentPage === 'how-to' ? 'bold' : 'normal',
                padding: '5px 10px',
                borderRadius: '4px',
                backgroundColor: currentPage === 'how-to' ? '#fef2f2' : 'transparent'
              }}
            >
              How To
            </button>
            <button 
              onClick={() => setCurrentPage('use-cases')} 
              style={{ 
                background: 'none', 
                border: 'none', 
                color: currentPage === 'use-cases' ? '#dc2626' : '#333', 
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: currentPage === 'use-cases' ? 'bold' : 'normal',
                padding: '5px 10px',
                borderRadius: '4px',
                backgroundColor: currentPage === 'use-cases' ? '#fef2f2' : 'transparent'
              }}
            >
              Use Cases
            </button>
          </div>
        </div>
      </nav>

      {/* Render current page */}
      {renderPage()}
    </div>
  );
}

export default App;

// Render the app
const root = createRoot(document.getElementById('root'));
root.render(<App />);